import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import ProductCard from "./ProductCard";
import LibraryAddProduct from "./LibraryAddProduct";
import ProductDetail from "./ProductDetail";
import LibraryCartBar from "./LibraryCartBar";
import LibraryCartQuickViewModal from "./LibraryCartQuickViewModal";
import SearchBar from "./SearchBar";
import LabelFilter from "./LabelFilter";
import LabelManager from "./LabelManager";
import { useGlobalState } from "../GlobalContext";
import fetchLibraryProductPrice from "./fetchLibraryProductPrice";
import { getLibraryCartItems } from "./libraryCartUtils.jsx";
import { toast } from "react-toastify";

const PRODUCTS_PER_PAGE = 12;

export default function LibraryPage({ textData, navigateToPageName }) {
    const { loggedInUser } = useGlobalState();
    const [products, setProducts] = useState([]);
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddPanel, setShowAddPanel] = useState(false);
    const [showLabelManager, setShowLabelManager] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedLabelIds, setSelectedLabelIds] = useState([]);
    const [sortOrder, setSortOrder] = useState("newest");
    const [visibleCount, setVisibleCount] = useState(PRODUCTS_PER_PAGE);

    // Selection + detail
    const [selectedProductIds, setSelectedProductIds] = useState(new Set());
    const [detailProduct, setDetailProduct] = useState(null);
    const [cartItems, setCartItems] = useState(() => getLibraryCartItems());
    const [showCartQuickView, setShowCartQuickView] = useState(false);

    const labelId = window.localStorage.getItem("label_id");
    const baseUrl = window.location.origin + `/api/${labelId}/library`;

    const fetchData = useCallback(() => {
        if (!labelId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        Promise.all([axios.get(`${baseUrl}/products`), axios.get(`${baseUrl}/labels`)])
            .then(([productsRes, labelsRes]) => {
                setLabels(labelsRes.data);
                // Enrich dynamic-price products with discount_multiplier from youropps
                const raw = productsRes.data.map((p) => ({
                    ...p,
                    has_fixed_price: p.price !== null && p.price !== undefined,
                }));
                console.log(
                    "[Library] API raw products:",
                    raw.map((p) => ({ id: p.id, name: p.name, price: p.price, has_fixed_price: p.has_fixed_price }))
                );
                Promise.all(raw.map((p) => fetchLibraryProductPrice(p))).then((enriched) => {
                    console.log(
                        "[Library] After enrichment:",
                        enriched.map((p) => ({
                            id: p.id,
                            name: p.name,
                            price: p.price,
                            has_fixed_price: p.has_fixed_price,
                            discount_multiplier: p.discount_multiplier,
                        }))
                    );
                    setProducts(enriched);
                    setLoading(false);
                });
            })
            .catch((error) => {
                console.error("Failed to load library data:", error);
                setLoading(false);
            });
    }, [baseUrl]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Lock body scroll on mobile when a slideover is open
    useEffect(() => {
        const isMobile = window.innerWidth <= 520;
        const isOpen = showAddPanel || !!detailProduct || showCartQuickView;
        if (isMobile && isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [showAddPanel, detailProduct, showCartQuickView]);

    useEffect(() => {
        const syncCartItems = () => {
            setCartItems(getLibraryCartItems());
        };

        window.addEventListener("storage", syncCartItems);
        return () => {
            window.removeEventListener("storage", syncCartItems);
        };
    }, []);

    // Client-side filtering + sorting
    const filteredProducts = useMemo(() => {
        let result = products.filter((p) => !p.hidden_in_library);

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            result = result.filter((p) => p.name.toLowerCase().includes(q));
        }

        if (selectedLabelIds.length > 0) {
            result = result.filter((p) => p.labels && p.labels.some((l) => selectedLabelIds.includes(l.id)));
        }

        const numericPrice = (p) => {
            const v = parseFloat(p.price);
            return isNaN(v) ? Infinity : v;
        };

        result = [...result].sort((a, b) => {
            switch (sortOrder) {
                case "oldest":
                    return new Date(a.created_at) - new Date(b.created_at);
                case "name_asc":
                    return a.name.localeCompare(b.name);
                case "name_desc":
                    return b.name.localeCompare(a.name);
                case "price_asc":
                    return numericPrice(a) - numericPrice(b);
                case "price_desc":
                    return numericPrice(b) - numericPrice(a);
                case "newest":
                default:
                    return new Date(b.created_at) - new Date(a.created_at);
            }
        });

        return result;
    }, [products, searchQuery, selectedLabelIds, sortOrder]);

    // Reset pagination when filters change
    useEffect(() => {
        setVisibleCount(PRODUCTS_PER_PAGE);
    }, [searchQuery, selectedLabelIds]);

    const visibleProducts = filteredProducts.slice(0, visibleCount);
    const hasMore = visibleCount < filteredProducts.length;

    const handleProductAdded = (newProduct) => {
        setProducts((prev) => [newProduct, ...prev]);
        setShowAddPanel(false);
    };

    const handleDeleteSelectedProducts = async () => {
        const ids = Array.from(selectedProductIds);
        if (ids.length === 0) return;

        const results = await Promise.allSettled(ids.map((id) => axios.delete(`${baseUrl}/products/${id}`)));
        const failedIds = [];

        results.forEach((result, index) => {
            if (result.status === "rejected") {
                failedIds.push(ids[index]);
            }
        });

        const deletedIds = ids.filter((id) => !failedIds.includes(id));

        if (deletedIds.length > 0) {
            const deletedSet = new Set(deletedIds);
            setProducts((prev) => prev.filter((p) => !deletedSet.has(p.id)));
        }

        setSelectedProductIds(new Set(failedIds));

        if (failedIds.length === 0) {
            toast.success(`${deletedIds.length} ${deletedIds.length === 1 ? "product" : "products"} deleted`);
            return;
        }

        if (deletedIds.length > 0) {
            toast.warning(
                `${deletedIds.length} deleted, ${failedIds.length} failed. Please try deleting remaining items again.`
            );
            return;
        }

        toast.error("Failed to delete selected products. Please try again.");
    };

    const handleToggleLabel = (labelId) => {
        if (labelId === null) {
            setSelectedLabelIds([]);
            return;
        }
        setSelectedLabelIds((prev) =>
            prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
        );
    };

    // Selection handlers
    const handleToggleSelect = (productId) => {
        setSelectedProductIds((prev) => {
            const next = new Set(prev);
            if (next.has(productId)) {
                next.delete(productId);
            } else {
                next.add(productId);
            }
            return next;
        });
    };

    const handleProductClick = (product) => {
        setDetailProduct(product);
    };

    const handleProductUpdated = (updatedProduct) => {
        setProducts((prev) => prev.map((p) => (p.id === updatedProduct.id ? updatedProduct : p)));
        setDetailProduct(null);
    };

    const handleProductDeleted = (productId) => {
        setProducts((prev) => prev.filter((p) => p.id !== productId));
        setSelectedProductIds((prev) => {
            const next = new Set(prev);
            next.delete(productId);
            return next;
        });
        setDetailProduct(null);
    };

    const handleAddedToCart = () => {
        setSelectedProductIds(new Set());
        setCartItems(getLibraryCartItems());
    };

    const handleOpenCartQuickView = () => {
        setDetailProduct(null);
        setShowCartQuickView(true);
    };

    const handleCartChanged = (nextItems) => {
        setCartItems(nextItems);
    };

    useEffect(() => {
        if (showCartQuickView && cartItems.length === 0) {
            setShowCartQuickView(false);
        }
    }, [showCartQuickView, cartItems]);

    // Select-all derived state
    const selectableProducts = useMemo(() => filteredProducts, [filteredProducts]);

    const isAllSelected =
        selectableProducts.length > 0 && selectableProducts.every((p) => selectedProductIds.has(p.id));
    const isSomeSelected = !isAllSelected && selectableProducts.some((p) => selectedProductIds.has(p.id));

    const selectAllRef = useRef(null);
    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = isSomeSelected;
        }
    }, [isSomeSelected]);

    const handleToggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedProductIds(new Set());
        } else {
            setSelectedProductIds(new Set(selectableProducts.map((p) => p.id)));
        }
    };

    // Resolve selected product objects for cart bar
    const selectedProducts = useMemo(() => {
        return products.filter((p) => selectedProductIds.has(p.id));
    }, [products, selectedProductIds]);

    return (
        <div className="library-page">
            <div className="library-header">
                <h2>Product Library</h2>
                <button className="library-add-button" onClick={() => setShowAddPanel(true)}>
                    + Add product
                </button>
            </div>

            {!loading && products.length > 0 && (
                <div className="library-filters">
                    <SearchBar value={searchQuery} onChange={setSearchQuery} />
                    <div className="library-filter-row">
                        <label className="library-select-all-checkbox">
                            <input
                                ref={selectAllRef}
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={handleToggleSelectAll}
                            />
                        </label>
                        <LabelFilter
                            labels={labels}
                            selectedLabelIds={selectedLabelIds}
                            onToggleLabel={handleToggleLabel}
                            onManageLabels={() => setShowLabelManager(true)}
                            onClearLabels={() => handleToggleLabel(null)}
                        />
                        <select
                            className="library-sort-select"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                        >
                            <option value="newest">Nieuwste eerst</option>
                            <option value="oldest">Oudste eerst</option>
                            <option value="name_asc">Naam A→Z</option>
                            <option value="name_desc">Naam Z→A</option>
                            <option value="price_asc">Prijs laag→hoog</option>
                            <option value="price_desc">Prijs hoog→laag</option>
                        </select>
                    </div>
                </div>
            )}

            {loading && (
                <div className="library-loading">
                    <div className="spinner"></div>
                </div>
            )}

            {!loading && products.length === 0 && (
                <div className="library-empty">
                    <p>No products yet. Add your first product.</p>
                    <button className="library-add-button" onClick={() => setShowAddPanel(true)}>
                        + Add your first product
                    </button>
                </div>
            )}

            {!loading && products.length > 0 && filteredProducts.length === 0 && (
                <div className="library-empty">
                    <p>No products found for these filters.</p>
                </div>
            )}

            {!loading && visibleProducts.length > 0 && (
                <>
                    <div className="library-grid">
                        {visibleProducts.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onClick={handleProductClick}
                                onSelect={handleToggleSelect}
                                isSelected={selectedProductIds.has(product.id)}
                            />
                        ))}
                    </div>
                    {hasMore && (
                        <div className="library-load-more">
                            <button
                                className="library-btn-load-more"
                                onClick={() => setVisibleCount((prev) => prev + PRODUCTS_PER_PAGE)}
                            >
                                Load more ({filteredProducts.length - visibleCount} remaining)
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Cart bar */}
            <LibraryCartBar
                selectedProducts={selectedProducts}
                cartItems={cartItems}
                onViewCartItems={handleOpenCartQuickView}
                onClearSelection={() => setSelectedProductIds(new Set())}
                onAddedToCart={handleAddedToCart}
                onDeleteSelected={handleDeleteSelectedProducts}
                navigateToCheckout={() =>
                    navigateToPageName(textData?.["urls/checkout"]?.["unique_name"] || "checkout")
                }
            />

            <LibraryCartQuickViewModal
                open={showCartQuickView}
                cartItems={cartItems}
                onClose={() => setShowCartQuickView(false)}
                onCartChanged={handleCartChanged}
                navigateToCheckout={() =>
                    navigateToPageName(textData?.["urls/checkout"]?.["unique_name"] || "checkout")
                }
            />

            {/* Add product slide-over */}
            {showAddPanel && (
                <LibraryAddProduct
                    onClose={() => setShowAddPanel(false)}
                    onProductAdded={handleProductAdded}
                    onLabelCreated={(label) => setLabels((prev) => [...prev, label])}
                    textData={textData}
                    labels={labels}
                    products={products}
                />
            )}

            {/* Edit product slide-over */}
            {detailProduct && (
                <ProductDetail
                    product={detailProduct}
                    onClose={() => setDetailProduct(null)}
                    onProductUpdated={handleProductUpdated}
                    onProductDeleted={handleProductDeleted}
                    onLabelCreated={(label) => setLabels((prev) => [...prev, label])}
                    textData={textData}
                    labels={labels}
                    navigateToCheckout={() =>
                        navigateToPageName(textData?.["urls/checkout"]?.["unique_name"] || "checkout")
                    }
                    navigateToQuotations={() =>
                        navigateToPageName(textData?.["urls/quotations"]?.["unique_name"] || "quotations")
                    }
                />
            )}

            {showLabelManager && (
                <LabelManager
                    labels={labels}
                    setLabels={setLabels}
                    onClose={() => setShowLabelManager(false)}
                    presetColors={loggedInUser?.company?.label_preset_colors}
                    onLabelDeleted={(id) => {
                        setProducts((prev) => prev.map((p) => ({ ...p, labels: p.labels.filter((l) => l.id !== id) })));
                        setSelectedLabelIds((prev) => prev.filter((lid) => lid !== id));
                    }}
                    onLabelUpdated={(updatedLabel) => {
                        setProducts((prev) =>
                            prev.map((p) => ({
                                ...p,
                                labels: p.labels.map((l) => (l.id === updatedLabel.id ? updatedLabel : l)),
                            }))
                        );
                    }}
                />
            )}
        </div>
    );
}
